import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { Search } from "lucide-react";

interface SelectPaymentTermModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (paymentTerm: any) => void;
  emitenteId?: number;
}

export function SelectPaymentTermModal({ open, onClose, onSelect, emitenteId }: SelectPaymentTermModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  
  const { data: paymentTerms, isLoading } = trpc.paymentTerms.list.useQuery(
    emitenteId ? { emitenteId } : undefined,
    { enabled: open && !!emitenteId },
  );

  const filteredPaymentTerms = paymentTerms?.filter((term: any) => {
    if (!term.ativo) return false;
    if (!searchTerm) return true;
    return term.descricao.toLowerCase().includes(searchTerm.toLowerCase());
  }) || [];

  const calculateDescriptive = (prazoPrimeira: number, prazoEntre: number, quantidade: number) => {
    const parcelas = [];
    for (let i = 0; i < quantidade; i++) {
      if (i === 0) {
        parcelas.push(prazoPrimeira);
      } else {
        parcelas.push(prazoPrimeira + (prazoEntre * i));
      }
    }
    return parcelas.join(", ");
  };

  const calculateDescriptiveForTerm = (term: any) => {
    let prazosPersonalizados: number[] = [];
    try {
      const parsed = Array.isArray(term.prazosParcelas) ? term.prazosParcelas : JSON.parse(String(term.prazosParcelas || "[]"));
      prazosPersonalizados = Array.isArray(parsed) ? parsed.map(Number).filter((prazo) => Number.isInteger(prazo) && prazo >= 0) : [];
    } catch {
      prazosPersonalizados = [];
    }
    return prazosPersonalizados.length > 0
      ? prazosPersonalizados.join(", ")
      : calculateDescriptive(term.prazoPrimeiraParcela, term.prazoEntreParcelas, term.quantidadeParcelas);
  };

  const handleSelect = (paymentTerm: any) => {
    onSelect(paymentTerm);
    onClose();
    setSearchTerm("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ width: '80%', maxWidth: '80%' }}>
        <DialogHeader>
          <DialogTitle>Selecionar Condição de Pagamento</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Carregando...</div>
          ) : filteredPaymentTerms.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma condição de pagamento encontrada
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">ID</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="w-[100px] text-center">Parcelas</TableHead>
                    <TableHead>Descritivo de Prazo (dias)</TableHead>
                    <TableHead className="w-[120px] text-center">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPaymentTerms.map((term: any) => (
                    <TableRow key={term.id}>
                      <TableCell className="font-medium">{term.id}</TableCell>
                      <TableCell>{term.descricao}</TableCell>
                      <TableCell className="text-center">{term.quantidadeParcelas}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {calculateDescriptiveForTerm(term)}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          onClick={() => handleSelect(term)}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Selecionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
